import { createFileRoute } from "@tanstack/react-router";
import { Page, SectionEyebrow, LinkedText } from "@/components/SiteLayout";

export const Route = createFileRoute("/clubregels")({
  head: () => ({
    meta: [
      { title: "Clubregels — Hi Coach" },
      { name: "description", content: "De clubregels van Hi Coach Club." },
      { property: "og:title", content: "Clubregels — Hi Coach" },
      { property: "og:description", content: "De clubregels van Hi Coach Club." },
      { property: "og:url", content: "/clubregels" },
    ],
    links: [{ rel: "canonical", href: "/clubregels" }],
  }),
  component: Clubregels,
});

const intro: string[] = [
  "Bij Hi Coach Club draait alles om plezier, respect, veiligheid en samen groeien. Ons doel is om schermtijd in te ruilen voor beweging, vriendschap en plezier op het veld, als basis voor een leven lang sporten.",
  "Onze clubvriendjes geven het goede voorbeeld:\nSem – 6 jaar: Stoere atleet. Hij traint voor de Kidsrun van 1 kilometer en staat voor moed, doorzettingsvermogen, uitdaging en medespelers helpen.\nMaya – 4 jaar: Slimme ontdekker. Ze is super bewegingsvaardig geworden door haar Peuter beweegdiploma en staat voor rust, respect, goed luisteren, verhalen en gezonde voeding.",
  "Sem en Maya beleven het liefst samen met coaches, ouders, verzorgers en vriendjes quality time op het veld. Uren achter een scherm zitten levert alleen maar verveling op. Daarom maken ze liever nieuwe vriendjes, behalen ze samen beweegdiploma's en maken ze volop plezier. Samen laten we zien dat bewegen een feest is, waarbij volwassenen het mooiste voorbeeld vormen voor de kinderen in de zaal.",
  "Als aangesloten KNGU-lid meldt Hi Coach Club alle kinderen aan bij de bond om onze kwaliteit, veiligheid en ons sportklimaat te borgen.",
];

const groepen: { kop: string; color: string; regels: { title: string; body: string }[] }[] = [
  {
    kop: "Voorbereiding & aanvang",
    color: "var(--rb-blue)",
    regels: [
      {
        title: "1. Kleding & Schoenen",
        body: "Het officiële Hi Coach Club sportshirt is verplicht.\nZaal uitsluitend betreedbaar op schone binnenschoenen zonder zwarte zolen of op blote voeten. Straatschoenen blijven buiten.",
      },
      {
        title: "2. Op tijd komen & Startkring",
        body: "Wees 5 minuten voor aanvang omgekleed aanwezig voor de startkring met een voorleesverhaal.",
      },
    ],
  },
  {
    kop: "Tijdens de les",
    color: "var(--rb-green)",
    regels: [
      {
        title: "3. Quality time op het veld",
        body: "Peuters van 2 tot 3 jaar: Één ouder of verzorger doet actief mee als co-coach.\nKleuters van 4 tot 6 jaar: Sporten zelfstandig op weg naar het diploma. Ouders mogen meedoen voor het plezier.",
      },
      {
        title: "4. Positief aanmoedigen & Respect voor Coaches",
        body: "Aanmoedigen gebeurt oordeelloos en positief, zoals \"Goed geprobeerd!\" of \"Lekker bezig!\". Dwingende of boze taal hoort hier niet thuis.\nToon respect voor onze coaches, die hun werk met hart en ziel uitvoeren.",
      },
      {
        title: "5. Materiaal & Sporthal",
        body: "Gebruik toestellen alleen na toestemming van de coach en zoals bedoeld.",
      },
      {
        title: "6. Eten, Drinken & Fruit",
        body: "Geen eten of drinken op de zaalvloer. Water drinken uit een eigen bidon kan buiten de zaal. Geen zoetigheid.\nElk kind krijgt na de les een lekker stuk fruit mee.",
      },
    ],
  },
  {
    kop: "Veiligheid, respect & klimaat",
    color: "var(--rb-orange)",
    regels: [
      {
        title: "7. Veiligheid & Stop-signaal",
        body: "Veiligheidsregels gelden voor iedereen: coaches, ouders én kinderen.\nBij een fluitsignaal of opgestoken hand bevriest iedereen direct als een standbeeld.",
      },
      {
        title: "8. Sportiviteit & Respect",
        body: "Proberen is winnen en fouten maken mag. Pesten, schelden en duwen accepteren we niet. We helpen elkaar overeind.",
      },
      {
        title: "9. Veilig Sportklimaat & Melden Ongewenst Gedrag",
        body: "Grensoverschrijdend gedrag tolereren we niet. Onze coaches hebben een geldige VOG en werken volgens KNGU-gedragscodes.\nMeldpunt: Ervaar je ongewenst gedrag? Spreek de Hi Coaches aan, of bel of mail de leiding. Vertrouwelijk contact is ook mogelijk via de KNGU of het Centrum Veilige Sport Nederland via 0900-2025590 of [centrumveiligesport.nl](https://centrumveiligesport.nl).",
      },
    ],
  },
  {
    kop: "Afsluiting & algemene afspraken",
    color: "var(--rb-red)",
    regels: [
      {
        title: "10. Opruimen & Schoon achterlaten",
        body: "Na afloop ruimt iedereen mee op. We laten de zaal en kleedkamers netjes achter.",
      },
      {
        title: "11. Positieve Afsluiting",
        body: "We sluiten elke les op een positieve manier af om het plezier en het groepsgevoel te vieren.",
      },
      {
        title: "12. Eigendommen & Aansprakelijkheid",
        body: "Laat waardevolle spullen thuis. Hi Coach Club is niet aansprakelijk voor diefstal, verlies of beschadiging.",
      },
      {
        title: "13. Privacy & Beeldmateriaal",
        body: "Foto's of video's van je eigen kind zijn voor privégebruik. Beeldmateriaal van andere kinderen publiceren mag alleen met toestemming van hun ouders.",
      },
      {
        title: "14. Betalingen & Samen oplossen",
        body: "Lukt een betaling even niet? Blijf er niet mee zitten. Wij denken vertrouwelijk mee over oplossingen, zoals gemeentelijke regelingen of sportfondsen.",
      },
    ],
  },
  {
    kop: "Samen bouwen aan de toekomst",
    color: "var(--rb-violet)",
    regels: [
      {
        title: "15. De Volgende Stap in Sport",
        body: "Na het 6e jaar kennen onze coaches de bewegingsvoorkeuren van jouw kind door en door. Vraag gerust welke vervolgsport het beste past.",
      },
      {
        title: "16. Help & Steun Ons via Tips & Tops",
        body: "Scan de gelamineerde QR-code op de muur om jouw tips en tops te delen. Elke input is welkom om samen te bouwen aan de topatleten van de toekomst!",
      },
    ],
  },
];

const slot =
  "Investeren in een leven lang plezier in bewegen. Onze sociale boodschap: Schermen uit, energie aan!";

function Clubregels() {
  return (
    <Page>
      <section className="pt-24 pb-10 bg-white">
        <div className="mx-auto max-w-3xl px-6">
          <SectionEyebrow color="var(--rb-blue)">Clubregels</SectionEyebrow>
          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">Clubregels</h1>
        </div>
      </section>

      <section className="pb-20 bg-white">
        <div className="mx-auto max-w-3xl px-6 text-lg text-muted-foreground">
          <div className="space-y-5">
            {intro.map((p, i) => (
              <p key={i} className="whitespace-pre-line">
                <LinkedText text={p} />
              </p>
            ))}
          </div>

          <div className="mt-16 space-y-14">
            {groepen.map((groep) => (
              <div key={groep.kop}>
                <div className="mb-6">
                  <span
                    className="block h-1.5 w-16 rounded-full mb-3"
                    style={{ background: groep.color }}
                  />
                  <h2 className="text-2xl md:text-3xl font-extrabold uppercase tracking-wide text-foreground">
                    {groep.kop}
                  </h2>
                </div>
                <div className="space-y-8">
                  {groep.regels.map((r) => (
                    <div key={r.title}>
                      <h3 className="text-xl font-extrabold text-foreground">{r.title}</h3>
                      <p className="mt-2 whitespace-pre-line">
                        <LinkedText text={r.body} />
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-16 whitespace-pre-line font-semibold text-foreground">
            <LinkedText text={slot} />
          </p>
        </div>
      </section>
    </Page>
  );
}
